import React, { useState, useMemo, useEffect } from 'react';
import { 
  BarChart3, Calendar, Filter, Search, 
  Plus, ArrowRight, ArrowLeft, Activity
} from 'lucide-react';
import { supabase } from '../../lib/supabase';

const MRPPlansList = ({ setActiveView, handleViewPlan, navigationSource = null, onReturnToWorkflows = null }) => {
  // State for filtering and sorting
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortField, setSortField] = useState('created_at');
  const [sortDirection, setSortDirection] = useState('desc');
  const [mrpPlans, setMrpPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Fetch MRP plans from Supabase
  useEffect(() => {
    const fetchMrpPlans = async () => {
      try {
        setLoading(true);
        
        const { data: plansData, error: plansError } = await supabase
          .from('mrp_plans')
          .select('*')
          .order('created_at', { ascending: false });

        if (plansError) {
          console.error('Error fetching MRP plans:', plansError);
          return;
        }

        setMrpPlans(plansData || []);
      } catch (error) {
        console.error('Error fetching MRP plans:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchMrpPlans();
  }, []);
  
  // Helper functions
  const getStatusDetails = (status) => {
    const statusMap = {
      'active': { name: '執行中', color: '#10B981' },
      'completed': { name: '已完成', color: '#059669' },
      'draft': { name: '草稿', color: '#6B7280' },
      'pending': { name: '待處理', color: '#3B82F6' },
      'delayed': { name: '延遲', color: '#F59E0B' },
      'cancelled': { name: '已取消', color: '#EF4444' }
    };

    return statusMap[status] || { name: '未知', color: '#6B7280' };
  };
  
  // Filtering and sorting for MRP plans
  const filteredPlans = useMemo(() => {
    return mrpPlans.filter(plan => {
      // Apply status filter
      if (statusFilter !== 'all' && plan.status !== statusFilter) {
        return false;
      }
      
      // Apply search term
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        return (
          plan.name.toLowerCase().includes(searchLower) ||
          (plan.description && plan.description.toLowerCase().includes(searchLower)) ||
          plan.planning_period.toLowerCase().includes(searchLower)
        );
      }
      
      return true;
    });
  }, [statusFilter, searchTerm, mrpPlans]);
  
  const sortedPlans = useMemo(() => {
    return [...filteredPlans].sort((a, b) => {
      let comparison = 0;
      
      switch (sortField) {
        case 'name':
          comparison = a.name.localeCompare(b.name);
          break;
        case 'planning_period':
          comparison = a.planning_period.localeCompare(b.planning_period);
          break;
        case 'start_date':
          comparison = new Date(a.start_date) - new Date(b.start_date);
          break;
        case 'status':
          comparison = a.status.localeCompare(b.status);
          break;
        case 'created_at':
          comparison = new Date(a.created_at) - new Date(b.created_at);
          break;
        default:
          comparison = 0;
      }
      
      return sortDirection === 'asc' ? comparison : -comparison;
    });
  }, [filteredPlans, sortField, sortDirection]);
  
  // Handle sort
  const handleSort = (field) => {
    if (field === sortField) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };
  
  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div className="flex items-center space-x-4">
          <h2 className="text-2xl font-bold text-gray-800">MRP計劃列表</h2>
          {navigationSource === 'workflows' && onReturnToWorkflows && (
            <button
              onClick={onReturnToWorkflows}
              className="flex items-center px-3 py-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-md transition-colors text-sm"
            >
              <ArrowLeft className="w-4 h-4 mr-1" />
              返回業務流程
            </button>
          )}
        </div>
        <div className="flex space-x-2">
          <button 
            onClick={() => setActiveView('dashboard')} 
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors flex items-center"
          >
            <BarChart3 className="w-4 h-4 mr-2" />
            返回儀表板
          </button>
          <button className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors flex items-center">
            <Plus className="w-4 h-4 mr-2" />
            新增計劃
          </button>
        </div>
      </div>
      
      {/* Header Image */}
      <div className="relative h-40 bg-gradient-to-r from-blue-500 to-blue-700 rounded-lg overflow-hidden shadow">
        <img 
          src="https://images.unsplash.com/photo-1664575602276-acd073f104c1?w=1200&h=300&fit=crop" 
          alt="MRP Planning" 
          className="w-full h-full object-cover mix-blend-overlay"
        />
        <div className="absolute inset-0 flex items-center justify-center">
          <h3 className="text-3xl font-bold text-white">物料需求計劃管理</h3>
        </div>
      </div>
      
      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow">
        <div className="flex flex-col md:flex-row md:items-center space-y-3 md:space-y-0 md:space-x-4">
          <div className="flex-1">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                placeholder="搜索計劃..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
          
          <div className="flex-shrink-0">
            <div className="relative inline-block text-left">
              <div className="flex items-center">
                <Filter className="mr-2 h-5 w-5 text-gray-400" />
                <select
                  className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <option value="all">所有狀態</option>
                  {Object.keys(getStatusDetails()).map(status => (
                    <option key={status} value={status}>
                      {getStatusDetails(status).name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Plans Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                  onClick={() => handleSort('name')}
                >
                  計劃名稱
                  {sortField === 'name' && (
                    <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                  )}
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                  onClick={() => handleSort('planning_period')}
                >
                  規劃期間
                  {sortField === 'planning_period' && (
                    <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                  )}
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                  onClick={() => handleSort('start_date')}
                >
                  開始日期
                  {sortField === 'start_date' && (
                    <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                  )}
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                  onClick={() => handleSort('status')}
                >
                  狀態
                  {sortField === 'status' && (
                    <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                  )}
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">操作</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan="6" className="px-6 py-4 text-center text-gray-500">加載中...</td>
                </tr>
              ) : filteredPlans.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-4 text-center text-gray-500">沒有找到相關的MRP計劃。</td>
                </tr>
              ) : sortedPlans.map((plan) => {
                const status = getStatusDetails(plan.status);
                return (
                  <tr 
                    key={plan.id} 
                    className="hover:bg-gray-50"
                  >
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{plan.name}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{plan.planning_period}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{plan.start_date}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <span 
                        className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full" 
                        style={{ backgroundColor: `${status.color}20`, color: status.color }}
                      >
                        {status.name}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button 
                        onClick={() => handleViewPlan(plan)}
                        className="text-blue-600 hover:text-blue-900 mr-2"
                      >
                        查看
                      </button>
                      <button className="text-gray-600 hover:text-gray-900">
                        編輯
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default MRPPlansList;
